// TechPage.js
import React from "react";
import {Box, Heading, Text, chakra, Stack} from "@chakra-ui/react";
import SectionCard from "../components/SectionCard";
import Highlight from "../components/Highlight";

const TechPage = () => {
    return (
        <Box bg="gray.50" py={8}>
            {/* 1. Smart Wearables with Edge AI */}
            <SectionCard flexDirection="row" delay={0}>
                <Box flex="2" textAlign="center">
                    <chakra.img
                        src="./Wearables_EdgeAI.png"
                        alt="Wearables Placeholder"
                        maxW="300px"
                        mx="auto"
                    />
                </Box>
                <Box flex="3">
                    <Heading as="h2" size="xl" mb={4}>
                        Smart Wearables with Edge AI
                    </Heading>
                    <Stack spacing={4}>
                        <Text fontSize="md">
                            <Highlight>Edge AI</Highlight> enables devices to do more than just sense — they interpret,
                            decide, and act in real time.
                        </Text>
                        <Text fontSize="md">
                            Using <Highlight>TinyML</Highlight>, we run trained neural networks directly on embedded
                            hardware like the <Highlight>ESP32</Highlight>, enabling on-device classification of
                            behaviors such as sniffing, resting, or active searching.
                        </Text>
                        <Text fontSize="md">
                            These devices communicate results via <Highlight>Bluetooth Low Energy (BLE)</Highlight>,
                            with no reliance on cloud connectivity — making them ideal for field use, even in remote
                            environments.
                        </Text>
                        <Text fontSize="md">
                            The result: <Highlight>low-latency, offline-capable systems</Highlight> that are
                            lightweight, power-efficient, and ready for deployment at scale.
                        </Text>
                    </Stack>
                </Box> </SectionCard>

            <SectionCard flexDirection="row-reverse" delay={0.1}>
                <Box flex="2" textAlign="center">
                    <chakra.img
                        src="./DeepLabCut_Web.png"
                        alt="Real-time pose estimation demo"
                        maxW="100%"
                        mx="auto"
                    />
                </Box>
                <Box flex="3">
                    <Heading as="h2" size="xl" mb={4}>
                        Computer Vision & Pose Estimation
                    </Heading>
                    <Stack spacing={4}>
                        <Text fontSize="md">
                            <Highlight>Computer vision</Highlight> is reshaping how we interpret behavior — and we've
                            brought it directly into the browser.
                        </Text>
                        <Text fontSize="md">
                            Our systems perform real-time pose tracking using <Highlight>custom-trained DeepLabCut
                            models</Highlight>, running at over 30 frames per second from a standard webcam feed.
                        </Text>
                        <Text fontSize="md">
                            A <Highlight>GPU-powered cloud instance</Highlight> handles the heavy lifting,
                            while <Highlight>WebRTC</Highlight> ensures low-latency video streaming between client and
                            backend.
                        </Text>
                        <Text fontSize="md">
                            The result: accurate, real-time behavioral feedback — with no software installation
                            required.
                        </Text>
                    </Stack>
                </Box>
            </SectionCard>

            {/* 3. Modular Systems with AI Agents */}
            <SectionCard flexDirection="row" delay={0.2}>
                <Box flex="2" textAlign="center">
                    <chakra.img
                        src="./TrainingPlanTeam.excalidraw_Section.png" // Adjust path as needed
                        alt="AI Agent System Overview"
                        maxW="100%"
                        mx="auto"
                    />
                </Box>
                <Box flex="3">
                    <Heading as="h2" size="xl" mb={4}>
                        Modular Systems with AI Agents
                    </Heading>
                    <Stack spacing={4}>
                        <Text fontSize="md">
                            <Highlight>AI agents</Highlight> are quickly becoming the next big shift in applied
                            artificial intelligence — enabling complex reasoning, collaboration, and decision-making
                            across tasks.
                        </Text>
                        <Text fontSize="md">
                            Unlike single-shot prompts or simple chatbots, agent-based systems combine multiple roles
                            with memory, iteration, and task specialization. It’s a modular and programmable approach to
                            solving real problems.
                        </Text>
                        <Text fontSize="md">
                            We’ve already brought such systems to the application level —
                            combining <Highlight>LangGraph</Highlight> orchestration, <Highlight>ChatGPT</Highlight>-powered
                            agents, <Highlight>WebSocket</Highlight>-based frontends, and <Highlight>AWS</Highlight>-backed
                            infrastructure into flexible, interactive platforms.
                        </Text>
                        <Text fontSize="md">
                            If you're exploring agent-based architectures or have a specific application in mind, we’d
                            love to hear from you.
                        </Text>
                    </Stack>
                </Box>
            </SectionCard>

            {/* 4. Large-Scale Data Analysis & Insight */}
            <SectionCard flexDirection="row-reverse" delay={0.3}>
                <Box flex="2" textAlign="center">
                    <chakra.img
                        src="./Data_analysis_3.png"
                        alt="Distribution analysis for keypoint likelihoods"
                        maxW="100%"
                        mx="auto"
                    />
                </Box>
                <Box flex="3">
                    <Heading as="h2" size="xl" mb={4}>
                        Large-Scale Data Analysis & Insight
                    </Heading>
                    <Stack spacing={4}>
                        <Text fontSize="md">
                            In complex systems, collecting data is only the first step — the real value comes from
                            making sense of it.
                        </Text>
                        <Text fontSize="md">
                            We analyze large, heterogeneous datasets from wearables, cameras, cloud logs, and
                            classification systems to identify patterns, validate predictions, and surface actionable
                            insights.
                        </Text>
                        <Text fontSize="md">
                            From per-keypoint likelihood distributions to physiological trends and classification
                            performance, our pipelines are built to scale and adapt across use cases.
                        </Text>
                        <Text fontSize="md">
                            The result: <Highlight>interpretable metrics</Highlight> that go beyond dashboards —
                            enabling better models, smarter decisions, and continuous improvement.
                        </Text>
                    </Stack>
                </Box>
            </SectionCard>


            {/* 5. Wireless Communication & IoT */}
            <SectionCard flexDirection="row" delay={0.4}>
                <Box flex="2" textAlign="center">
                    <chakra.img
                        src="./IoTSensor_NN.excalidraw.png"
                        alt="IoT system overview diagram"
                        maxW="100%"
                        mx="auto"
                    />
                </Box>
                <Box flex="3">
                    <Heading as="h2" size="xl" mb={4}>
                        Wireless Communication & IoT
                    </Heading>
                    <Stack spacing={4}>
                        <Text fontSize="md">
                            <Highlight>Reliable, real-time communication</Highlight> is the backbone of modern sensing
                            systems — and we build it to perform in the field.
                        </Text>
                        <Text fontSize="md">
                            Our architecture connects embedded devices to the cloud
                            via <Highlight>MQTT</Highlight> and <Highlight>AWS IoT Core</Highlight>, where <Highlight>IoT
                            rules</Highlight> trigger processing, storage, and API responses — all in milliseconds.
                        </Text>
                        <Text fontSize="md">
                            We integrate technologies like <Highlight>Bluetooth Low Energy
                            (BLE)</Highlight>, <Highlight>Ultra-Wideband (UWB)</Highlight>, and standard Internet
                            protocols to link wearables, smartphones, and cloud services in a unified system.
                        </Text>
                        <Text fontSize="md">
                            This infrastructure supports remote control, data flow, and intelligent feedback — securely
                            and at scale.
                        </Text>
                    </Stack>
                </Box> </SectionCard>
            {/* 6. Cloud-Connected App Development */}

            <SectionCard flexDirection="row-reverse" delay={0.5}>
                <Box flex="2" textAlign="center">
                    <chakra.img
                        src="./AppDev_TechStack.png"
                        alt="Cloud-connected app system diagram"
                        maxW="100%"
                        mx="auto"
                    />
                </Box>
                <Box flex="3">
                    <Heading as="h2" size="xl" mb={4}>
                        Cloud-Connected App Development
                    </Heading>
                    <Stack spacing={4}>
                        <Text fontSize="md">
                            Powerful systems deserve powerful interfaces — and we build full-stack applications that
                            bring advanced tech to the hands of users.
                        </Text>
                        <Text fontSize="md">
                            Whether it’s a mobile app for field deployment or a web dashboard for data review, our
                            apps are built to connect seamlessly with <Highlight>cloud
                            infrastructure</Highlight>, <Highlight>real-time databases</Highlight>, and custom APIs
                            — using modern frontend frameworks and mobile development environments.
                        </Text>
                        <Text fontSize="md">
                            <Highlight>AWS</Highlight> powers authentication, storage, and deployment, ensuring
                            performance, scalability, and security from backend to frontend.
                        </Text>
                        <Text fontSize="md">
                            The result: complete, connected solutions — ready to use, extend, or deploy at scale.
                        </Text>
                    </Stack>
                </Box>
            </SectionCard>
        </Box>
    );
};

export default TechPage;
